import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CoordinateTransformer } from "../coordinateTransformer";
import { CanvasLayout, Coordinate, DevicePin } from "../types";
import { colorForStatus, colorForType, labelForType } from "../colors";

interface LoadedLayout {
  layout: CanvasLayout;
  transformer: CoordinateTransformer;
  image: HTMLImageElement;
}

interface OverlayCanvasProps {
  layouts: CanvasLayout[];
  devices: DevicePin[];
  colorByStatus?: boolean;
  height?: number;
}

const PADDING = 24;
const MAX_ZOOM = 20;
const MIN_ZOOM = 0.01;

const OverlayCanvas: React.FC<OverlayCanvasProps> = ({
  layouts,
  devices,
  colorByStatus = false,
  height = 560,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loaded, setLoaded] = useState<LoadedLayout[]>([]);
  const [layoutOpacity, setLayoutOpacity] = useState(0.55);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState<Coordinate>({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState<Coordinate>({ x: 0, y: 0 });

  // Store the centred-at-zoom-1 panOffset so resetView can restore it
  const defaultPanRef = useRef<Coordinate>({ x: 0, y: 0 });

  const calibratedLayouts = useMemo(
    () =>
      layouts.filter(
        (l) => l.isCalibrated && l.imageUrl && l.calibrationPoints.length >= 2,
      ),
    [layouts],
  );

  // Load all images + build transformers
  useEffect(() => {
    let cancelled = false;
    const tasks = calibratedLayouts.map(
      (layout) =>
        new Promise<LoadedLayout | null>((resolve) => {
          let transformer: CoordinateTransformer;
          try {
            transformer = new CoordinateTransformer(layout.calibrationPoints);
          } catch {
            resolve(null);
            return;
          }
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve({ layout, transformer, image: img });
          img.onerror = () => resolve(null);
          img.src = layout.imageUrl;
        }),
    );
    Promise.all(tasks).then((results) => {
      if (cancelled) return;
      setLoaded(results.filter((r): r is LoadedLayout => r !== null));
    });
    return () => {
      cancelled = true;
    };
  }, [calibratedLayouts]);

  // Resize canvas to parent container (fixed size — zoom/pan are viewport controls)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      canvas.width = parent.clientWidth;
      canvas.height = height;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [height]);

  // Centre the view whenever the loaded data changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || loaded.length === 0) return;

    let minE = Infinity,
      maxE = -Infinity,
      minN = Infinity,
      maxN = -Infinity;
    const acc = (e: number, n: number) => {
      minE = Math.min(minE, e);
      maxE = Math.max(maxE, e);
      minN = Math.min(minN, n);
      maxN = Math.max(maxN, n);
    };
    loaded.forEach(({ image, transformer }) => {
      [
        { x: 0, y: 0 },
        { x: image.naturalWidth, y: 0 },
        { x: 0, y: image.naturalHeight },
        { x: image.naturalWidth, y: image.naturalHeight },
      ].forEach((c) => {
        const rw = transformer.imageToRealWorld(c);
        acc(rw.easting, rw.northing);
      });
    });
    devices.forEach((d) => acc(d.realWorld.easting, d.realWorld.northing));

    const spanE = maxE - minE || 1;
    const spanN = maxN - minN || 1;
    const base = Math.min(
      (canvas.width - 2 * PADDING) / spanE,
      (canvas.height - 2 * PADDING) / spanN,
    );

    // panOffset absorbs the centering so the wheel-zoom formula stays simple:
    //   screen_x = (e - minE) * base * zoom + panOffset.x
    const centreX = (canvas.width - spanE * base) / 2;
    const centreY = (canvas.height - spanN * base) / 2;
    defaultPanRef.current = { x: centreX, y: centreY };
    setZoom(1);
    setPanOffset({ x: centreX, y: centreY });
  }, [loaded, devices]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (loaded.length === 0) return;

    // ── Real-world bounding box ──────────────────────────────────────────────
    let minE = Infinity,
      maxE = -Infinity,
      minN = Infinity,
      maxN = -Infinity;
    const acc = (e: number, n: number) => {
      minE = Math.min(minE, e);
      maxE = Math.max(maxE, e);
      minN = Math.min(minN, n);
      maxN = Math.max(maxN, n);
    };
    loaded.forEach(({ image, transformer }) => {
      [
        { x: 0, y: 0 },
        { x: image.naturalWidth, y: 0 },
        { x: 0, y: image.naturalHeight },
        { x: image.naturalWidth, y: image.naturalHeight },
      ].forEach((c) => {
        const rw = transformer.imageToRealWorld(c);
        acc(rw.easting, rw.northing);
      });
    });
    devices.forEach((d) => acc(d.realWorld.easting, d.realWorld.northing));

    const spanE = maxE - minE || 1;
    const spanN = maxN - minN || 1;

    // base: fits content into canvas at zoom=1
    const base = Math.min(
      (canvas.width - 2 * PADDING) / spanE,
      (canvas.height - 2 * PADDING) / spanN,
    );

    // ── Single unified coordinate formula (grid, images, devices all use this) ──
    // screen_x = (e - minE) * base * zoom + panOffset.x
    // screen_y = (maxN - n) * base * zoom + panOffset.y
    const rw2c = (e: number, n: number): Coordinate => ({
      x: (e - minE) * base * zoom + panOffset.x,
      y: (maxN - n) * base * zoom + panOffset.y,
    });

    // ── 1. HK1980 grid ───────────────────────────────────────────────────────
    if (showGrid) {
      const maxSpan = Math.max(spanE, spanN);
      if (maxSpan > 0) {
        const rawStep = maxSpan / 8;
        const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const residual = rawStep / mag;
        const nice =
          residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
        const gridStep = nice * mag;

        ctx.save();
        ctx.strokeStyle = "rgba(13, 110, 253, 0.35)";
        ctx.fillStyle = "rgba(13, 110, 253, 0.9)";
        ctx.lineWidth = 1;
        ctx.font = "10px sans-serif";

        const startE = Math.ceil(minE / gridStep) * gridStep;
        for (let e = startE; e <= maxE; e += gridStep) {
          const p1 = rw2c(e, minN);
          const p2 = rw2c(e, maxN);
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
          ctx.fillText(`E ${Math.round(e)}`, p1.x + 2, p1.y - 2);
        }
        const startN = Math.ceil(minN / gridStep) * gridStep;
        for (let n = startN; n <= maxN; n += gridStep) {
          const p1 = rw2c(minE, n);
          const p2 = rw2c(maxE, n);
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
          ctx.fillText(`N ${Math.round(n)}`, p1.x + 2, p1.y - 2);
        }
        ctx.restore();
      }
    }

    // ── 2. Layout images ─────────────────────────────────────────────────────
    // Derive the canvas setTransform params from the unified formula above.
    // Image pixel (ix, iy) → real-world (e, n) via transformer:
    //   e = (a(ix-tx) + b(iy-ty)) / det
    //   n = (b(ix-tx) - a(iy-ty)) / det
    // Substituting into rw2c:
    //   screen_x = (e - minE)*base*zoom + panOffset.x
    //            = base*zoom*a/det * ix + base*zoom*b/det * iy
    //              + base*zoom*(-a*tx - b*ty)/det - base*zoom*minE + panOffset.x
    const S = base * zoom;
    loaded.forEach(({ image, transformer }) => {
      const { a, b, tx, ty } = transformer.getParams();
      const det = a * a + b * b;
      if (det === 0) return;

      ctx.save();
      ctx.globalAlpha = layoutOpacity;
      ctx.setTransform(
        /* m11 */ (S * a) / det,
        /* m12 */ (-S * b) / det,
        /* m21 */ (S * b) / det,
        /* m22 */ (S * a) / det,
        /* dx  */ S * (-(a * tx + b * ty) / det - minE) + panOffset.x,
        /* dy  */ S * (maxN + (b * tx - a * ty) / det) + panOffset.y,
      );
      ctx.drawImage(image, 0, 0);
      ctx.restore();
    });
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // ── 3. Device markers (same rw2c formula, no separate ctx transform) ─────
    devices.forEach((d) => {
      const p = rw2c(d.realWorld.easting, d.realWorld.northing);
      const color = colorByStatus
        ? colorForStatus(d.alertStatus)
        : colorForType(d.type);

      ctx.save();
      ctx.fillStyle = color;
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      if (d.name) {
        ctx.save();
        ctx.font = "10px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        const lx = p.x + 10;
        const ly = p.y;
        const tw = ctx.measureText(d.name).width;
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.fillRect(lx - 1, ly - 7, tw + 4, 14);
        ctx.fillStyle = "#333";
        ctx.fillText(d.name, lx, ly);
        ctx.restore();
      }
    });
  }, [
    loaded,
    devices,
    layoutOpacity,
    colorByStatus,
    zoom,
    panOffset,
    showGrid,
  ]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (e.button === 0) {
      setIsDragging(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    const dx = e.clientX - lastMousePos.x;
    const dy = e.clientY - lastMousePos.y;
    setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const resetView = () => {
    setZoom(1);
    setPanOffset(defaultPanRef.current);
  };

  // Wheel handler with zoom-to-cursor functionality
  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mouseX = (event.clientX - rect.left) * (canvas.width / rect.width);
      const mouseY = (event.clientY - rect.top) * (canvas.height / rect.height);
      const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * zoomFactor));
      const zoomPointX = (mouseX - panOffset.x) / zoom;
      const zoomPointY = (mouseY - panOffset.y) / zoom;
      setZoom(newZoom);
      setPanOffset({
        x: mouseX - zoomPointX * newZoom,
        y: mouseY - zoomPointY * newZoom,
      });
    },
    [zoom, panOffset],
  );

  // Attach wheel handler directly to DOM to prevent page scroll
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel]);

  if (calibratedLayouts.length === 0) {
    return (
      <div className="text-muted" style={{ padding: 24 }}>
        No calibrated layouts to overlay yet.
      </div>
    );
  }

  return (
    <div className="sl-canvas-wrap">
      <canvas
        ref={canvasRef}
        className="sl-canvas"
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          maxWidth: "100%",
          height: "auto",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      />
      <div className="sl-toolbar">
        <button
          type="button"
          className="btn btn-sm"
          title="Zoom in"
          onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z * 1.2))}
        >
          <i className="bi bi-zoom-in" />
        </button>
        <button
          type="button"
          className="btn btn-sm"
          title="Zoom out"
          onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z * 0.8))}
        >
          <i className="bi bi-zoom-out" />
        </button>
        <button
          type="button"
          className="btn btn-sm"
          title="Reset view"
          onClick={resetView}
        >
          <i className="bi bi-arrows-fullscreen" />
        </button>
        <button
          type="button"
          className={`btn btn-sm ${showGrid ? "btn-primary" : ""}`}
          title="Toggle HK1980 grid"
          onClick={() => setShowGrid((g) => !g)}
        >
          <i className="bi bi-grid-3x3" />
        </button>
        <label className="sl-opacity" title="Layout opacity">
          <i className="bi bi-circle-half" />
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.05}
            value={layoutOpacity}
            onChange={(e) => setLayoutOpacity(Number(e.target.value))}
          />
        </label>
      </div>
      {devices.length > 0 && (
        <div className="sl-legend">
          {colorByStatus ? (
            <>
              <span>
                <i className="sl-dot" style={{ background: "#28a745" }} />{" "}
                Within bounds
              </span>
              <span>
                <i className="sl-dot" style={{ background: "#ffc107" }} /> Alert
              </span>
              <span>
                <i className="sl-dot" style={{ background: "#fd7e14" }} /> Alarm
              </span>
              <span>
                <i className="sl-dot" style={{ background: "#dc3545" }} />{" "}
                Action
              </span>
            </>
          ) : (
            Array.from(new Set(devices.map((d) => d.type))).map((t) => (
              <span key={t}>
                <i className="sl-dot" style={{ background: colorForType(t) }} />
                {labelForType(t)}
              </span>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default OverlayCanvas;
